import moment from 'moment';
import { month, ordinal_suffix_of } from '../components/common/Utils';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

const getDayName = (dayIndex) => {
  const days = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
  ];
  return days[dayIndex];
};
const daysOfWeek = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
];
const getFullDate = (date) => {
  if (!date) {
    return null;
  }
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();

  return year + '-' + month + '-' + day;
};

/**
 * Returns the next date after Today which is not in blockedDates
 * @param blockedDates {Array<string>} A list of un-available dates in the format (yyyy-mm-dd) (or any format the
 *                                    {@link https://momentjs.com/docs/#/parsing/string/ moment library} can parse)
 */
const getNextAvailableDate = (blockedDates) => {
  const parsedDates = blockedDates
    .map((dateStr) => moment(dateStr))
    .sort((a, b) => b.diff(a));

  let date = moment();
  while (parsedDates.some((blocked) => blocked.isSame(date, 'day'))) {
    date.add(1, 'days');
  }
  return date.format('Y-MM-DD');
};

const formatDate = (date) => {
  return moment(date).format('DD/MM/YYYY');
};
const formatDateWithTime = (date) => {
  return moment(date).format('DD/MM/YYYY HH:mm');
};
const formatDateWithTime2 = (date) => {
  const mdate = moment(date);
  const format = mdate.minutes() > 0 ? 'h:mma - DD/MM/YYYY' : 'ha - DD/MM/YYYY';
  return mdate.format(format);
};

const formatDateTime = (timestamp) => {
  const d = dayjs(timestamp);
  const day = d.format('Do MMM. h:mm a');
  return day;
};

const formatShortDateTime = (timestamp) => {
  const d = new Date(timestamp);
  const day = d.getDate().toString().padStart(2, '0');
  const month = (d.getMonth() + 1).toString().padStart(2, '0');
  const time = d.toLocaleString('en-UK', {
    hour: 'numeric',
    hourCycle: 'h12',
  });
  return day + '/' + month + ' ' + time;
};

const getDatesBetween = (start, end) => {
  const startDate = moment(start);
  const endDate = moment(end);
  let dates = [];

  let currDate = startDate.startOf('day');
  let lastDate = endDate.startOf('day');

  while (currDate.add(1, 'days').diff(lastDate) < 0) {
    dates.push(normalizeTZOffset(currDate.clone().toDate()));
  }
  return dates;
};

// TODO unit test
const getDatesInMonthBeforeDate = (date) => {
  const startDate = moment(date).startOf('month').clone().subtract(1, 'day');
  const endDate = moment(date);
  let dates = [];

  let currDate = startDate.startOf('day');
  let lastDate = endDate.startOf('day');

  while (currDate.add(1, 'days').diff(lastDate) < 0) {
    dates.push(normalizeTZOffset(currDate.clone().toDate()));
  }
  return dates;
};

// TODO unit test
const getDatesInMonthAfterDate = (date) => {
  if (!date) {
    return [];
  }
  const endDate = moment(date).endOf('month').add(1, 'day').clone();
  const startDate = moment(date);
  let dates = [];

  let currDate = endDate.startOf('day');
  let lastDate = startDate.startOf('day');

  let diff = currDate.subtract(1, 'days').diff(lastDate);
  while (diff > 0) {
    if (currDate.date() === date.getDate()) {
      break;
    }
    dates.push(normalizeTZOffset(currDate.clone().toDate()));
    diff = currDate.subtract(1, 'days').diff(lastDate);
  }
  return dates;
};

const toUTCDate = (dateString) => {
  // Returns a date with time 00:00:00 GMT+000
  if (!dateString || !moment(dateString).isValid()) {
    return null;
  }
  let date;
  if (typeof dateString === 'string') {
    date = new Date(new Date(dateString).toISOString().substring(0, 23));
  } else {
    date = new Date(dateString.toISOString());
  }
  date.setTime(date.getTime() - date.getTimezoneOffset() * 60 * 1000);
  return date;
};

const toNonUTCDate = (date) => {
  if (!date) {
    return null;
  }
  return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
};

const normalizeTZOffset = (date) => {
  if (!date || !moment(date).isValid()) {
    return date;
  }
  let copy;
  if (typeof date === 'string') {
    copy = new Date(date);
  } else {
    copy = new Date(date.toISOString());
  }
  copy.setTime(copy.getTime() + Math.abs(copy.getTimezoneOffset() * 60 * 1000));
  return copy;
};

const getNextWeekday = (
  startDate,
  weekdayName,
  isDayToDay = false,
  minWeek = 0
) => {
  const currDate = new Date(startDate);
  currDate.setDate(currDate.getDate() + minWeek * 7);
  while (getDayName(currDate.getDay()) !== weekdayName) {
    currDate.setDate(currDate.getDate() + 1);
  }
  if (isDayToDay) {
    currDate.setDate(currDate.getDate() + 7);
  }

  return currDate;
};

const getAllDatesNotEqualToWeekday = (minDate, maxDate, weekdayName) => {
  const nonArrivalDays = [];
  const currDate = moment(new Date(minDate));
  while (currDate.isSameOrBefore(maxDate, 'day')) {
    if (getDayName(currDate.day()) !== weekdayName) {
      nonArrivalDays.push(new Date(currDate.toDate()));
    }
    currDate.add(1, 'day');
  }
  return nonArrivalDays;
};

const formatEventDate = (dateString) => {
  const date = dayjs(dateString);
  const day = date.format('D');
  const suffix =
    day % 10 === 1 && day !== '11'
      ? 'st'
      : day % 10 === 2 && day !== '12'
        ? 'nd'
        : day % 10 === 3 && day !== '13'
          ? 'rd'
          : 'th';
  return date.format(`dddd D[${suffix}] MMMM YYYY`);
};

const formatEventDatetime = (
  datetimeString,
  timezone = null,
  includeTz = false
) => {
  if (!timezone) {
    timezone = 'UTC';
  }
  if (!datetimeString) {
    return '';
  }
  const utcDatetime = dayjs.utc(datetimeString);
  const eventLocalTime = utcDatetime.tz(timezone);
  if (!eventLocalTime.isValid()) {
    console.error({
      datetimeString,
      timezone,
      includeTz,
      utcDatetime,
      eventLocalTime,
    });
    return eventLocalTime.toString(); // Will return 'Invalid date'
  }
  let tz = '';
  if (includeTz) {
    // This is used rather than dayjs.format('z') to give "BST" rather than "UTC+1"
    tz =
      ' ' +
      new Intl.DateTimeFormat('en-GB', {
        timeZone: timezone,
        timeZoneName: 'short',
      })
        .format(eventLocalTime.toDate())
        .split(' ')[1];
  }
  return (
    eventLocalTime
      .format(`h${eventLocalTime?.minute() ? ':mm' : ''}a`)
      .toLowerCase() + tz
  );
};

const formatEventTime = (
  timeString,
  timezone = null,
  dateString = null,
  includeTz = false
) => {
  if (!dateString) {
    dateString = dayjs().toISOString();
  }
  return formatEventDatetime(
    `${dateString}T${timeString}`,
    timezone,
    includeTz
  );
};

const getNext12Months = () => {
  const months = [];
  const today = new Date();

  for (let i = 0; i < 12; i++) {
    const future = new Date(today.getFullYear(), today.getMonth() + i, 1);
    const label = future.toLocaleString('default', { month: 'long' });
    const year = future.getFullYear();
    months.push({ label, year, id: `${label}-${year}` });
  }

  return months;
};

const calculateEndDateFromSession = (startDate, sessionType) => {
  if (!startDate) return null;

  const start = dayjs(startDate);

  switch (sessionType) {
    case 'Day Session':
      return start.toDate(); // Same day
    case 'Overnight':
      return start.add(1, 'day').toDate(); // Next day
    case '24hr':
      return start.add(1, 'day').toDate(); // 1 day
    case '48hr':
      return start.add(2, 'day').toDate(); // 2 days
    case '5 Day':
      return start.add(5, 'day').toDate(); // 5 days total (start + 5)
    case '7 Day':
      return start.add(7, 'day').toDate(); // 7 days total (start + 7)
    default:
      return start.toDate();
  }
};

dayjs.extend(customParseFormat);

// Strict, so an impossible date like 2026-02-30 is rejected rather than
// rolling over to March.
const parseIsoDate = (value) => {
  const date = dayjs(value ?? '', 'YYYY-MM-DD', true);
  return date.isValid() ? date : null;
};

export {
  getDayName,
  parseIsoDate,
  daysOfWeek,
  getFullDate,
  formatDate,
  formatDateWithTime,
  formatDateWithTime2,
  getNextAvailableDate,
  getNextWeekday,
  getNext12Months,
  getAllDatesNotEqualToWeekday,
  formatDateTime,
  formatShortDateTime,
  getDatesBetween,
  getDatesInMonthBeforeDate,
  getDatesInMonthAfterDate,
  toUTCDate,
  toNonUTCDate,
  normalizeTZOffset,
  formatEventDate,
  formatEventTime,
  formatEventDatetime,
  calculateEndDateFromSession,
};
